import {cloneDeep, orderBy} from 'lodash'
import type {ActionPlanOrder} from '~/models/actionPlanOrder'
import {getDefaultActionDate} from './deliveryDates'
import {
    ACTIONPLAN_PLANTING_TYPES_NEITHER,
    ACTIONPLAN_REALLOCATED_AREA_THINNING,
    ACTIONPLAN_TYPES,
    ACTIONPLAN_TYPES_PLANTING,
    ACTIONPLAN_TYPES_PLANTSALE,
    getActionPlanProductAdditivePlanting,
    getActionPlanStandardProduct
} from '~/constants/actionPlans'
import {getPlantsPerAcreFromPlantingStandard, setDefaultActionPlanData} from './actionPlans'
import {CONTRACT_TYPE_FOREST_OPERATION, CONTRACT_TYPE_SUPPLY_WOOD} from '~/constants/contracts'
import {
    ALLMA_JOB_TYPE_ADDITIVE_PLANTING,
    ALLMA_JOB_TYPE_HOGGER,
    ALLMA_JOB_TYPE_PLANTING,
    ALLMA_JOB_TYPE_THINNING,
    ALLMA_JOB_TYPE_YOUNG_TREE_CARE,
    EXCLUDE_ALLMA_JOB_TYPES
} from '~/constants/allmaJobs'
import type {AllmaAction, AllmaJob} from '~/models/allmajob'
import type {ActionPlan, FullContract} from '~/models/contract'
import type {TimelineItem} from '~/models/util'
import {roundToEven} from "~/helpers/numberHelpers";

export const getAllmaJobsByContractType = (contractType: number, allmaJobs: AllmaJob[]): AllmaJob[] => {
  const excludeJobTypes = EXCLUDE_ALLMA_JOB_TYPES.find(t => t.contractType === contractType) || null
  return allmaJobs.filter((job: AllmaJob) => !excludeJobTypes || !excludeJobTypes.jobTypes.includes(job.JobType))
}

export const setCommonActionFields = (contract: FullContract, job: AllmaJob, actions: AllmaAction[], subType?: number, actionDate?: Date, productId?: string, plantsPerAcre?: number, supplementingUpTo?: number): AllmaAction[] => {
  actions.forEach((action: AllmaAction) => {
    if (subType) {
      action.SubType = subType
      action.ActionDate = getDefaultActionDate(job.JobType, contract)?.toISOString()
    }
    if (actionDate) {
      action.ActionDate = actionDate.toISOString()
    }
    if (productId) {
      action.ProductId = productId
    }
    if (plantsPerAcre) {
      action.PlantsPerAcre = plantsPerAcre
    }
    if (supplementingUpTo) {
      action.SupplementingUpTo = supplementingUpTo
    }
  })
  return actions
}

export const jobRequiresActionPlan = (job: AllmaJob): boolean => {
  const jobsTypesRequiringActionPlan = [
    ALLMA_JOB_TYPE_HOGGER,
    ALLMA_JOB_TYPE_THINNING
  ]

  return jobsTypesRequiringActionPlan.includes(job.JobType)
}

export const setDefaultActionFields = (contract: FullContract, job: AllmaJob, action: AllmaAction, isNew: boolean): AllmaAction => {
  if (job.JobType === ALLMA_JOB_TYPE_PLANTING || job.JobType === ALLMA_JOB_TYPE_ADDITIVE_PLANTING || job.JobType === ALLMA_JOB_TYPE_YOUNG_TREE_CARE || job.JobType === ALLMA_JOB_TYPE_HOGGER || job.JobType === ALLMA_JOB_TYPE_THINNING) {
    if (![ALLMA_JOB_TYPE_YOUNG_TREE_CARE, ALLMA_JOB_TYPE_HOGGER].includes(job.JobType)) {
      // overwrite plant sale product id with product standard
      action.PlantSaleProductId = job.JobType === ALLMA_JOB_TYPE_ADDITIVE_PLANTING ? getActionPlanProductAdditivePlanting.value : getActionPlanStandardProduct.value
    }
    const actionPlan: ActionPlan = {
      SiteQuality: action.SiteQuality,
      Wood: action.Wood,
      AdditivePlanting: job.JobType === ALLMA_JOB_TYPE_ADDITIVE_PLANTING,
      YoungTreecare: job.JobType === ALLMA_JOB_TYPE_YOUNG_TREE_CARE
    }
    const plantsPerAcre = getPlantsPerAcreFromPlantingStandard(actionPlan)
    if (plantsPerAcre) {
      if (job.JobType === ALLMA_JOB_TYPE_ADDITIVE_PLANTING && !action.SupplementingUpTo) {
        action.SupplementingUpTo = plantsPerAcre
      } else if (!action.PlantsPerAcre) {
        action.PlantsPerAcre = plantsPerAcre
      }
    }
    if (action.Type?.toUpperCase()?.includes('TRESLAGSBLANDING')) {
      action.WoodMixRatio = 0.5
    }
  }
  if (jobRequiresActionPlan(job)) {
    if (isNew) { // set default values on actionplan if first time fetch
      action.ActionPlan = setDefaultActionPlanData(contract)
      if (job.JobType === ALLMA_JOB_TYPE_THINNING) {
        action.ActionPlan.Planting = ACTIONPLAN_PLANTING_TYPES_NEITHER
        action.ActionPlan.ReallocatedArea = ACTIONPLAN_REALLOCATED_AREA_THINNING
      }
    } else { // copy silviculture from existing action when fetching again
      const existingAction = job.Actions && Array.isArray(job.Actions)
        ? job.Actions.find((a: AllmaAction) => a.Id === action.Id)
        : null
      if (existingAction && existingAction.ActionPlan) {
        action.ActionPlan = cloneDeep(existingAction.ActionPlan)
      } else {
        action.ActionPlan = setDefaultActionPlanData(contract)
      }
    }
    action.ActionPlan.SiteQuality = action.SiteQuality
    action.ActionPlan.Wood = action.Wood
    action.ActionPlan.PlantsPerAcre = action.PlantsPerAcre
    action.ActionPlan.SupplementingUpTo = action.SupplementingUpTo
  } else if (contract.Type === CONTRACT_TYPE_FOREST_OPERATION || contract.Type === CONTRACT_TYPE_SUPPLY_WOOD) {
    action.AdditionalOrder = true
  }
  return action
}

export const getAllmaJobTimeline = (contract: FullContract, allmaJobs: AllmaJob[]): TimelineItem[] => {
  const list = [] as TimelineItem[]

  const felling = {
    key: `hogger_${contract.LoggingMonth}_${contract.Year}`,
    title: 'FELLING',
    date: new Date(contract.Year, contract.LoggingMonth, 1),
    icon: 'spot/felling',
    subItems: []
  }

  const thinning = {
    key: `thinning_${contract.LoggingMonth}_${contract.Year}`,
    title: 'THINNING',
    date: new Date(contract.Year, contract.LoggingMonth, 1),
    icon: 'spot/thinning',
    subItems: []
  }

  allmaJobs.forEach((allmaJob: AllmaJob) => {
    if (allmaJob.Actions) {
      allmaJob.Actions.forEach((allmaAction: AllmaAction) => {
        if (allmaJob.JobType === ALLMA_JOB_TYPE_THINNING) {
          thinning.subItems.push(`${allmaAction.Plot}/${allmaAction.Stand}`)
        }
        if (allmaJob.JobType === ALLMA_JOB_TYPE_HOGGER) {
          felling.subItems.push(`${allmaAction.Plot}/${allmaAction.Stand}`)
        }
        const type = ACTIONPLAN_TYPES.find(x => x.allmaJobId === allmaJob.JobType) || null
        if (type) {
          const key = `${type.Name}_${new Date(allmaAction.ActionDate).getMonth()}_${new Date(allmaAction.ActionDate).getFullYear()}`
          const existing = list.find(x => x.key === key) || null
          if (existing) {
            existing.subItems.push(`${allmaAction.Plot}/${allmaAction.Stand}`)
          } else {
            list.push({
              key,
              title: type.Name,
              date: new Date(allmaAction.ActionDate),
              icon: type.Icon,
              subItems: [`${allmaAction.Plot}/${allmaAction.Stand}`]
            })
          }
        }
      })

      // TODO use getActionPlanTimeline on allmaAction.ActionPlan (if type is hogger or thinning)
    }
  })

  if (felling.subItems.length > 0) {
    list.push(felling)
  }

  if (thinning.subItems.length > 0) {
    list.push(thinning)
  }

  return orderBy(list, 'date')
}

export const getOrdersFromAllmaJobs = (jobs: AllmaJob[], isAdditionalOrder: boolean): ActionPlanOrder[] => {
  const orders: ActionPlanOrder[] = []
  ACTIONPLAN_TYPES.forEach((type) => {
    jobs.forEach((job: AllmaJob) => {
      if (job.Actions) {
        job.Actions.forEach((action: AllmaAction) => {
          if ((job.JobType === type.allmaJobId || (action.ActionPlan && action.ActionPlan[type.Field] && action.ActionPlan[type.Field] === type.checkValue)) && (action.AdditionalOrder === isAdditionalOrder || (action.ActionPlan && action.ActionPlan.AdditionalOrder === isAdditionalOrder)) && !job.Removed) {
            if (job.JobType !== ALLMA_JOB_TYPE_HOGGER && job.JobType !== ALLMA_JOB_TYPE_THINNING && type.Id === ACTIONPLAN_TYPES_PLANTING && action.SubType === 2) {
              return
            }
            if (job.JobType !== ALLMA_JOB_TYPE_HOGGER && job.JobType !== ALLMA_JOB_TYPE_THINNING && type.Id === ACTIONPLAN_TYPES_PLANTSALE && action.SubType !== 2) {
              return
            }
            let volume = 0
            let volumeSecond = 0
            switch (type.TypeOfVolume) {
              case 'AREA_DAA':
                volume = action.Area
                break
              case 'NUMBER_OF_PLANTS_WITH_VOLUME':
                if (job.JobType === ALLMA_JOB_TYPE_HOGGER || job.JobType === ALLMA_JOB_TYPE_THINNING) {
                  volume = action.ActionPlan.PlantsPerAcre ? action.Area * action.ActionPlan.PlantsPerAcre : action.Area
                } else {
                  volume = action.PlantsPerAcre ? action.Area * action.PlantsPerAcre : action.Area
                }
                break
              case 'NUMBER_OF_METERS_WITH_VOLUME':
                volume = job.JobType === ALLMA_JOB_TYPE_HOGGER || job.JobType === ALLMA_JOB_TYPE_THINNING ? action.ActionPlan.Meters : action.Meters
                break
              case 'AREA_DAA_NUMBER_OF_PLANTS':
                volume = volume = action.Area
                if (job.JobType === ALLMA_JOB_TYPE_HOGGER || job.JobType === ALLMA_JOB_TYPE_THINNING) {
                  volumeSecond = action.ActionPlan.PlantsPerAcre ? action.Area * action.ActionPlan.PlantsPerAcre : action.Area
                } else {
                  volumeSecond = action.PlantsPerAcre ? action.Area * action.PlantsPerAcre : action.Area
                }
                break
              default:
                break
            }
            const existingOrder = orders.find(o => o.Name === type.Name)
            if (existingOrder) {
              existingOrder.Volume = (existingOrder.Volume as number) + volume
              existingOrder.VolumeSecond = existingOrder.VolumeSecond + volumeSecond
            } else {
              orders.push({
                Name: type.Name,
                Text: type.TypeOfVolume,
                Volume: volume,
                VolumeSecond: volumeSecond
              })
            }
          }
        })
      }
    })
  })
  return orders
}

/**
 *
 * @param action
 *
 */
export const getNumberOfPlants = (action: AllmaAction) => {
  const isWoodMix = action.Type?.toUpperCase()?.includes('TRESLAGSBLANDING')
  const woodMixRatio = action.WoodMixRatio || 1
  const ordinaryArea = action.Area! * woodMixRatio
  const woodMixArea = action.Area! * (1 - woodMixRatio)
  const numOrdinaryPlants = roundToEven(ordinaryArea * action.PlantsPerAcre!)
  const numWoodMixPlants = isWoodMix ? roundToEven(woodMixArea * action.WoodMixPlantsPerAcre!) : 0
  const numPlantsTotal = numOrdinaryPlants + numWoodMixPlants

  return { numOrdinaryPlants, numWoodMixPlants, numPlantsTotal }
}

/**
 *
 * @param action
 *
 */
export const getArea = (action: AllmaAction) => {
  const woodMixRatio = action.WoodMixRatio || 1
  const ordinaryArea = action.Area! * woodMixRatio
  const woodMixArea = action.Area! * (1 - woodMixRatio)

  return { ordinaryArea, woodMixArea }
}
